/* eslint-disable @next/next/no-img-element */
"use client";
import Markdown from "react-markdown";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";
import {
  FeatureGuide,
  getTouchpointGuides,
  Guides,
  TouchpointItem,
} from "@/stores/slices/guideSlice";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { externalMarkdownLink } from "@/components/md-components";
import { useRouter } from "next/navigation";

type TouchpointGuidesDialogProps = {
  item: TouchpointItem;
  onDismiss: (item: TouchpointItem) => Promise<void> | void;
};

export function TouchpointGuidesDialog({
  item,
  onDismiss,
}: TouchpointGuidesDialogProps) {
  const router = useRouter();
  const [open, setOpen] = useState(true);
  const guides = getTouchpointGuides(item);

  useEffect(() => {
    setOpen(true);
  }, [item.id]);

  const handleDismiss = useCallback(async () => {
    setOpen(false);
    await onDismiss(item);
  }, [item, onDismiss]);

  const handleDone = useCallback(async (type: string) => {
    if (type === 'coupons') {
      router.push('/workflow-creator');
    }
    setOpen(false);
    await onDismiss(item);
  }, [item, onDismiss, router]);

  const renderGuidesContent = useCallback((guides: Guides) => {
    if (guides?.type === 'features') {
      return (
        <FeatureGuides
          guides={guides.value}
          onDone={() => handleDone(guides.type)}
        />
      );
    }
    if (guides?.type === 'gift') {
      return (
        <MeetingGiftGuides
          url={guides?.value?.url}
          onDone={() => handleDone(guides.type)}
        />
      );
    }
    if (guides?.type === 'coupons') {
      return (
        <MeetingCouponsGuides
          url={guides?.value?.url}
          onDone={() => handleDone(guides.type)}
        />
      );
    }
    if (guides?.type === 'ai_col_charge') {
      return (
        <CommonImageContent
          {...guides?.value}
          onDone={() => handleDone(guides.type)}
        />
      );
    }
    return null;
  }, [handleDone]);

  if (!guides?.type || ![
    'features', 'gift', 'coupons', 'ai_col_charge'
  ].includes(guides?.type)) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={async (open) => {
        setOpen(open);
        if (!open) {
          await handleDismiss();
        }
      }}
    >
      <DialogContent
        className={cn(
          "w-full sm:max-w-fit p-0 m-0 h-auto border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 overflow-hidden gap-0",
        )}
        tabIndex={-1}
        onPointerDownOutside={(evt) => {
          evt.preventDefault();
        }}
        onOpenAutoFocus={(evt) => {
          evt.preventDefault();
        }}
      >
        {renderGuidesContent(guides)}
        <DialogClose />
      </DialogContent>
    </Dialog>
  );
}

type FeatureGuidesProps = {
  guides: FeatureGuide[];
  onDone: () => void;
}

const FeatureGuides = ({
  guides: featureGuides,
  onDone = () => { },
}: FeatureGuidesProps) => {
  const [currentPage, setCurrentPage] = useState(0);

  const goToNextPage = () => {
    if (currentPage < featureGuides.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="max-h-fit h-full w-full flex items-center justify-center overflow-hidden bg-background focus-visible:outline-none focus-visible:ring-0 max-w-[720px]" tabIndex={-1}>
      <div className="max-h-fit h-full flex transition-transform duration-300 max-w-full" style={{ transform: `translateX(-${currentPage * 100}%)` }}>
        {featureGuides.map((feature, index) => (
          <div key={index} className="max-h-fit h-full w-full flex-shrink-0 items-center justify-center">
            <div className="w-full aspect-video">
              <img
                src={feature.url}
                alt={feature.caption}
                className="object-cover h-full"
              />
            </div>
            <div className="p-7">
              <div className="flex justify-between">
                <h2 className="font-medium text-2xl">{feature.caption}</h2>
                <span className="font-normal text-base text-muted-foreground">{currentPage + 1}/{featureGuides.length}</span>
              </div>
              <div className="font-normal text-base text-muted-foreground mt-3.5"><Markdown components={externalMarkdownLink}>{feature.description}</Markdown></div>
              <div className="flex space-x-5 items-center justify-center mt-7">
                {
                  (currentPage === 0) ? null : (
                    <Button
                      className="w-1/3"
                      variant="outline" size="sm"
                      onClick={goToPrevPage}
                    >
                      上一页
                    </Button>
                  )
                }

                {
                  (currentPage === featureGuides.length - 1) ?
                    (
                      <Button
                        className="w-1/3"
                        variant="default"
                        size="sm"
                        onClick={() => onDone()}
                      >
                        我知道啦
                      </Button>
                    )
                    : (
                      <Button
                        className="w-1/3"
                        variant="default"
                        size="sm"
                        onClick={goToNextPage}
                      >
                        下一页
                      </Button>
                    )
                }
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MeetingGiftGuides = ({
  // url = publicAsset('/images/dashboard-guides-meeting-gift.png?v=2'),
  // url = 'https://oss.findinai.cn/asset/0226/banlance1.png',
  // url = "https://oss.findinai.cn/storage/20250418/c9b422a64f9cfa1030c2fb3f50ee85ae.png",
  url = "https://oss.findinai.cn/storage/20251218/d5e87d0dfcfb24e6935ef7490ace14ba.png",
  onDone = () => { },
}: {
  url?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const scale = (Number(elRef.current?.offsetWidth) || 800) / 800;
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={802}
        height={554}
        alt="首次见面，送你 100 飞豆 免费体验"
      />
      <Button
        className="absolute w-[378px] h-[40px]"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        立即使用
      </Button>
    </div>
  );
};

const MeetingCouponsGuides = ({
  url = "https://oss.findinai.cn/storage/20250418/270ca59e852e2d82543d3d398930e5e4.png",
  onDone = () => { },
}: {
  url?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const scale = (Number(elRef.current?.offsetWidth) || 800) / 800;
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={969}
        height={831}
        alt="给新朋友一份礼物，文献综述首单减免400豆"
      />
      <Button
        className="absolute w-[240px] h-[48px]"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        立即使用
      </Button>
    </div>
  );
};

export type UserTriggeredDialogProps = {
  children: ReactNode;
  height?: number;
  width?: number;
  url: string;
  description?: string;
  onDone?: () => void;
  label?: string;
}
export function UserTriggeredDialog({
  children,
  width = 1950,
  height = 1780,
  url = "https://oss.findinai.cn/storage/20250522/12604fbdb0bc26ee33de7fd2a75a1443.jpeg",
  description = '',
  onDone = () => { },
  label,
}: UserTriggeredDialogProps) {
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  const renderGuidesContent = useCallback(() => {
    if (!url) {
      return <></>;
    }
    return (
      <CommonImageContent
        width={width}
        height={height}
        url={url}
        description={description}
        onDone={() => {
          setOpen(false);
          onDone();
        }}
        label={label}
      />
    )
  }, [url, width, height, description, label, onDone]);

  return (
    <Dialog
      open={!!open}
      onOpenChange={async (open) => {
        setOpen(open);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className={cn(
          "p-0 m-0",
          "w-fit h-auto sm:max-w-fit border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 overflow-hidden gap-0",
        )}
        tabIndex={-1}
        onPointerDownOutside={(evt) => {
          evt.preventDefault();
        }}
        onOpenAutoFocus={(evt) => {
          evt.preventDefault();
        }}
      >
        {
          renderGuidesContent()
        }
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
}

const CommonImageContent = ({
  width = 1950,
  height = 1780,
  url = "https://oss.findinai.cn/storage/20250522/12604fbdb0bc26ee33de7fd2a75a1443.jpeg",
  description = '',
  label = '',
  onDone = () => { },
}: {
  height?: number;
  width?: number;
  url?: string;
  description?: string;
  label?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const x = (Number(elRef.current?.offsetWidth) || 800) / 800;
    const y = (Number(elRef.current?.offsetHeight) || 600) / 600;
    const scale = Math.min(x, y);
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={width}
        height={height}
        alt={description}
      />
      <Button
        size="lg"
        className="absolute w-[240px] h-[48px] text-lg"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        {label || '我知道了'}
      </Button>
    </div>
  );
};
